import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Assuming db is exported from firebase config
import { useAuth } from '../contexts/AuthContext'; // Import useAuth
import { UserProfileContainer, Avatar, UserInfo, UserName, UserEmail } from '../styles/UserProfilePage.styles';
import Loader from '../components/Loader'; // Assuming a Loader component exists

const UserProfilePage = () => {
    const { userId } = useParams(); // This is the ID of the profile being viewed
    const { user: currentUser } = useAuth(); // This is the currently authenticated user
    const [userData, setUserData] = useState(null); // Data of the profile being viewed
    const [profilePageBackground, setProfilePageBackground] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPageData = async () => {
            if (!userId) {
                setError("User ID for profile is missing.");
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                let fetchedUserData = null;
                let fetchedBackground = null;

                // Fetch profile user's data
                const userRef = doc(db, 'users', userId);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    fetchedUserData = userSnap.data();
                } else {
                    setError('User not found.');
                    setLoading(false);
                    return;
                }

                // Fetch current logged-in user's settings for background
                if (currentUser && currentUser.uid) {
                    const settingsRef = doc(db, 'userSettings', currentUser.uid);
                    const settingsSnap = await getDoc(settingsRef);
                    if (settingsSnap.exists()) {
                        fetchedBackground = settingsSnap.data().background;
                    }
                }

                setUserData(fetchedUserData);
                setProfilePageBackground(fetchedBackground);

            } catch (err) {
                console.error("Error fetching page data:", err);
                setError('Failed to fetch page data.');
            } finally {
                setLoading(false);
            }
        };

        fetchPageData();
    }, [userId, currentUser]);

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <UserProfileContainer><p>{error}</p></UserProfileContainer>;
    }

    if (!userData) {
        // This case should ideally be covered by the error 'User not found'
        // but as a fallback:
        return <UserProfileContainer><p>User data is not available.</p></UserProfileContainer>;
    }

    const containerStyle = profilePageBackground ? {
        background: `url(${profilePageBackground?.urls?.full || profilePageBackground}) center center / cover no-repeat fixed`,
    } : {
        // Default background if no user preference is set or found
        // Or keep it transparent to inherit from a global style, if any
        backgroundColor: '#1D2125', // Default to app's dark background
    };

    return (
        <UserProfileContainer style={containerStyle}>
            <Avatar src={userData.photoURL || '/default-avatar.png'} alt={userData.displayName || 'User'} />
            <UserInfo>
                <UserName>{userData.displayName || 'N/A'}</UserName>
                <UserEmail>{userData.email || 'N/A'}</UserEmail>
                {/* Add more user details here as needed */}
            </UserInfo>
        </UserProfileContainer>
    );
};

export default UserProfilePage;
